import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { TIMELINE_HORIZONTAL_PADDING } from './constants';
import {
  ItemMeta,
  TransitionMeta,
  useDragOverlay,
} from './drag-overlay-provider';
import { EditorStarterItem } from './items/item-type';
import { TimelineItemDragOverlay } from './timeline/timeline-item/timeline-item-drag-overlay';
import {
  getItemLeftOffset,
  getItemRoundedPosition,
  getItemWidth,
} from './utils/position-utils';
import { timelineScrollableContainerRef } from './utils/restore-scroll-after-zoom';
import { useAllItems } from './utils/use-context';
import { Z_INDEX_DRAG_OVERLAY } from './z-indices';

const DragOverlayItem: React.FC<{
  item: EditorStarterItem;
  meta: ItemMeta;
  cursorPosition: { x: number; y: number };
  timelineWidth: number;
  visibleFrames: number;
  trackMuted: boolean;
  snappedFrame: number | undefined;
}> = ({
  item,
  meta,
  cursorPosition,
  timelineWidth,
  visibleFrames,
  trackMuted,
  snappedFrame,
}) => {
  const style: React.CSSProperties = useMemo(() => {
    let left: number;

    if (snappedFrame !== undefined) {
      // snapping 시 snap된 frame을 기반으로 절대 위치를 계산
      const containerRect =
        timelineScrollableContainerRef.current?.getBoundingClientRect();
      if (!containerRect) {
        throw new Error('Container rect not found');
      }
      const itemLeft = getItemLeftOffset({
        timelineWidth,
        totalDurationInFrames: visibleFrames,
        from: snappedFrame,
      });
      left = containerRect.left + itemLeft + TIMELINE_HORIZONTAL_PADDING;
    } else {
      // snapping 없음, offset과 함께 cursor 위치 사용
      left =
        cursorPosition.x - meta.initialOffset.x + TIMELINE_HORIZONTAL_PADDING;
    }

    return {
      position: 'fixed',
      top: cursorPosition.y - meta.initialOffset.y,
      left,
      pointerEvents: 'none',
      zIndex: Z_INDEX_DRAG_OVERLAY,
    };
  }, [cursorPosition, meta, snappedFrame, timelineWidth, visibleFrames]);

  return (
    <div
      key={meta.id}
      style={style}
    >
      <TimelineItemDragOverlay
        item={item}
        timelineWidth={timelineWidth}
        visibleFrames={visibleFrames}
        height={meta.height}
        trackMuted={trackMuted}
      />
    </div>
  );
};

// 트랜지션 드래그 오버레이의 높이
const TRANSITION_OVERLAY_HEIGHT = 20;

const DragOverlayTransition: React.FC<{
  meta: TransitionMeta;
  cursorPosition: { x: number; y: number };
  timelineWidth: number;
  visibleFrames: number;
  snappedFrame: number | undefined;
}> = ({ meta, cursorPosition, timelineWidth, visibleFrames, snappedFrame }) => {
  const style: React.CSSProperties = useMemo(() => {
    let left: number;

    if (snappedFrame !== undefined) {
      // snapping 시 snap된 frame을 기반으로 절대 위치 계산
      const containerRect =
        timelineScrollableContainerRef.current?.getBoundingClientRect();
      if (!containerRect) {
        return { display: 'none' };
      }
      const transitionLeft = getItemLeftOffset({
        timelineWidth,
        totalDurationInFrames: visibleFrames,
        from: snappedFrame,
      });
      left = containerRect.left + transitionLeft + TIMELINE_HORIZONTAL_PADDING;
    } else {
      // snapping 없음, offset과 함께 cursor 위치 사용
      left =
        cursorPosition.x - meta.initialOffset.x + TIMELINE_HORIZONTAL_PADDING;
    }

    // 트랜지션 높이와 트랙 중앙 정렬을 위한 Y 오프셋 계산
    const trackCenterOffset =
      (meta.trackHeight - TRANSITION_OVERLAY_HEIGHT) / 2;
    const top = cursorPosition.y - meta.initialOffset.y + trackCenterOffset;

    return {
      position: 'fixed',
      top,
      left,
      pointerEvents: 'none',
      zIndex: Z_INDEX_DRAG_OVERLAY,
    };
  }, [cursorPosition, meta, snappedFrame, timelineWidth, visibleFrames]);

  // 트랜지션 너비 계산
  const transitionWidth = useMemo(() => {
    const rawWidth = getItemWidth({
      itemDurationInFrames: meta.transition.durationInFrames,
      timelineWidth,
      totalDurationInFrames: visibleFrames,
    });
    const { width } = getItemRoundedPosition(0, rawWidth);
    return Math.max(width, 4);
  }, [meta.transition.durationInFrames, timelineWidth, visibleFrames]);

  return (
    <div style={style}>
      <div
        style={{
          width: transitionWidth,
          height: TRANSITION_OVERLAY_HEIGHT,
          backgroundColor: '#8B5CF6',
          borderRadius: 4,
          border: '1px solid #7C3AED',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          opacity: 0.9,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            transform: 'rotate(45deg)',
          }}
        />
      </div>
    </div>
  );
};

const InnerDragOverlay: React.FC<{
  itemsMeta: ItemMeta[];
  transitionsMeta: TransitionMeta[];
  cursorPosition: { x: number; y: number };
  timelineWidth: number;
  visibleFrames: number;
  trackMuted: boolean;
  snappedPositions: Record<string, number> | null;
}> = ({
  itemsMeta,
  transitionsMeta,
  cursorPosition,
  timelineWidth,
  visibleFrames,
  trackMuted,
  snappedPositions,
}) => {
  const allItems = useAllItems();

  return createPortal(
    <>
      {itemsMeta.map((meta) => {
        const item = allItems.items[meta.id];
        if (!item || !item.isDraggingInTimeline) {
          return null;
        }

        const snappedFrame = snappedPositions?.[meta.id];

        return (
          <DragOverlayItem
            key={meta.id}
            item={item}
            meta={meta}
            cursorPosition={cursorPosition}
            timelineWidth={timelineWidth}
            visibleFrames={visibleFrames}
            trackMuted={trackMuted}
            snappedFrame={snappedFrame}
          />
        );
      })}
      {transitionsMeta.map((meta) => (
        <DragOverlayTransition
          key={meta.id}
          meta={meta}
          cursorPosition={cursorPosition}
          timelineWidth={timelineWidth}
          visibleFrames={visibleFrames}
          snappedFrame={snappedPositions?.[meta.id]}
        />
      ))}
    </>,
    document.body,
  );
};

export const DraggingTimelineItems: React.FC = () => {
  const {
    isDragging,
    draggedItemIds,
    itemsMeta,
    transitionsMeta,
    cursorPosition,
    timelineWidth,
    visibleFrames,
    snappedPositions,
  } = useDragOverlay();

  if (!isDragging || draggedItemIds.length === 0 || !cursorPosition) {
    return null;
  }

  return (
    <InnerDragOverlay
      itemsMeta={itemsMeta}
      transitionsMeta={transitionsMeta}
      cursorPosition={cursorPosition}
      timelineWidth={timelineWidth}
      visibleFrames={visibleFrames}
      trackMuted={false}
      snappedPositions={snappedPositions}
    />
  );
};
